import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Breadcrumb, Card, Button, Table, Tag, Modal, Space, Typography, message, Form, Input } from 'antd';
import * as Icon from 'react-feather';
import { Link } from 'react-router-dom';
import {
  termsActionStart,
  termsActionError,
  getAllTermsAction,
  createTermsAction,
  expireTermsAction,
  activateTermsAction,
} from '../../core/data/redux/modules/termsAndConditions/actions';
import { getAllTerms, createTerms, expireTerms, activateTerms } from '../../services/adminServices/termsAndConditions';
import RichTextEditor from '../common/components/RichTextEditor';
import { prepareContentForAPI } from '../../utils/richText';

const { Title } = Typography;

const ListTerms = () => {
  const dispatch = useDispatch();
  const { terms, loading, error } = useSelector((state: any) => state.termsAndConditions || { terms: [], loading: false, error: null });
  const [tableData, setTableData] = useState<any[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  // Rich text editor states
  const [termsContentJson, setTermsContentJson] = useState<any>(null);
  const [termsHtml, setTermsHtml] = useState<string>('');

  useEffect(() => {
    fetchTerms();
    // eslint-disable-next-line
  }, []);

  const fetchTerms = async () => {
    dispatch(termsActionStart());
    try {
      const res = await getAllTerms();
      if (res.error) throw new Error(res.error || 'Error fetching terms');
      const data = Array.isArray(res.data) ? res.data : [];
      
      // Sort by version in descending order (newest first)
      const sortedData = data.sort((a, b) => {
        // Convert version strings to numbers for proper comparison
        const versionA = parseFloat(a.version) || 0;
        const versionB = parseFloat(b.version) || 0;
        return versionB - versionA;
      });
      
      dispatch(getAllTermsAction(sortedData));
      setTableData(sortedData);
    } catch (err: any) {
      dispatch(termsActionError(err.message));
      message.error(err.message);
    }
  };

  const handleAdd = () => {
    form.resetFields();
    setTermsContentJson(null);
    setTermsHtml('');
    setIsModalVisible(true);
  };

  const handleExpire = async (id: string) => {
    setActionLoading(id);
    try {
      await expireTerms(id);
      dispatch(expireTermsAction(id));
      message.success('Terms expired');
      fetchTerms();
    } catch (err: any) {
      message.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleActivate = async (id: string) => {
    setActionLoading(id);
    try {
      await activateTerms(id);
      dispatch(activateTermsAction(id));
      message.success('Terms activated');
      fetchTerms();
    } catch (err: any) {
      message.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        version: values.version,
        terms_conditions_text: prepareContentForAPI(termsContentJson, termsHtml),
      };
      const res = await createTerms(payload);
      if (res.error) throw new Error(res.error);
      dispatch(createTermsAction(res.data));
      message.success('Terms created');
      setIsModalVisible(false);
      fetchTerms();
    } catch (err: any) {
      if (err?.errorFields) return; // form validation error
      message.error(err.message);
    }
  };

  const columns = [
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: any) => {
        const status = record.is_expired ? 'expired' : record.is_active ? 'active' : 'inactive';
        return (
          <Tag color={status === 'active' ? 'green' : status === 'expired' ? 'red' : 'orange'}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Link to={`/admin/terms-and-conditions/terms-detail/${record.id}`}>
            <Button icon={<Icon.Eye size={16} />} size="small" />
          </Link>
          {!record.is_expired && (
            <Button icon={<Icon.StopCircle size={16} />} size="small" loading={actionLoading === record.id} onClick={() => handleExpire(record.id)}>
              Expire
            </Button>
          )}
          {!record.is_active && (
            <Button icon={<Icon.CheckCircle size={16} />} size="small" loading={actionLoading === record.id} onClick={() => handleActivate(record.id)}>
              Activate
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper page-settings" style={{ paddingTop: 0 }}>
      <div className="content">
        <Breadcrumb style={{ marginBottom: 20 }}>
          <Breadcrumb.Item>
            <Link to="/admin/terms-and-conditions/list-terms">Terms & Conditions</Link>
          </Breadcrumb.Item>
        </Breadcrumb>
        <Card>
          <Space style={{ marginBottom: 16 }}>
            <Button icon={<Icon.Plus size={16} />} type="primary" onClick={handleAdd}>
              Add Terms
            </Button>
          </Space>
          <Table
            columns={columns}
            dataSource={tableData}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </Card>
        <Modal
          title={'Add Terms'}
          open={isModalVisible}
          onOk={handleModalOk}
          onCancel={() => setIsModalVisible(false)}
          okText={'Create'}
          width={800}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="version"
              label="Version"
              rules={[{ required: true, message: 'Please enter version' }]}
            >
              <Input placeholder="Enter version (e.g. 1.0.0)" />
            </Form.Item>
             <Form.Item
               label="Terms & Conditions Text"
               rules={[{ required: true, message: 'Please enter terms & conditions text' }]}
             >
               <RichTextEditor
                 value={termsHtml}
                 onChange={(htmlContent, jsonContent) => {
                   setTermsHtml(htmlContent);
                   setTermsContentJson(jsonContent);
                 }}
                 placeholder="Enter terms & conditions text"
                 height={300}
               />
             </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default ListTerms; 